import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api.js';
import { ProductSelector } from '../components/visits/ProductSelector.js';
import { 
  BarChart3, TrendingUp, Package, Users, Filter, Download, Truck, FileText, 
  MapPin, Target, CheckCircle, Archive, LucideIcon, Info, RefreshCw
} from 'lucide-react';

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: number | string;
  subtitle?: string;
  icon: LucideIcon;
  color: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, subtitle, icon: Icon, color }) => {
  return (
    <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <div className={`p-3 rounded-xl ${color.replace('bg-', 'bg-opacity-10 bg-')}`}>
          <Icon size={24} className={color.replace('bg-', 'text-')} />
        </div>
        <div className="flex-1">
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">{title}</p>
          <p className="text-3xl font-black text-gray-900">{value}</p>
          {subtitle && <p className="text-xs text-gray-600 font-medium mt-1">{subtitle}</p>}
        </div>
      </div>
    </div>
  );
};

// Info Box Component
const InfoBox: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 flex items-start gap-3 no-print">
      <Info className="text-blue-600 flex-shrink-0 mt-0.5" size={20} />
      <div className="text-sm text-blue-900 leading-relaxed">{children}</div>
    </div>
  );
};

const Reports: React.FC = () => {
  const navigate = useNavigate();
  const [report, setReport] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    date_from: '',
    date_to: '',
    product_id: '',
    outcome: '',
  });
  const [clientName, setClientName] = useState('');
  
  // Auto-detect client name from selected product
  useEffect(() => {
    if (filters.product_id && report?.byProduct) {
      const selectedProduct = report.byProduct.find((p: any) => p.name);
      if (selectedProduct?.brand) {
        setClientName(selectedProduct.brand);
      }
    } else {
      setClientName('Field Sales Campaign');
    }
  }, [filters.product_id, report]);

  // Fetch report when filters change OR on mount
  useEffect(() => {
    fetchReport();
  }, [filters]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.product_id) params.append('product_id', filters.product_id);
      if (filters.outcome) params.append('outcome', filters.outcome);
      
      // Add cache-busting timestamp to force fresh data
      params.append('_t', Date.now().toString());

      const res = await api.get(`/reports/visits-summary?${params.toString()}`);
      setReport(res.data);
    } catch (err) {
      console.error('Failed to fetch report', err);
    } finally {
      setLoading(false);
    }
  };

  const exportCSV = () => {
    if (!report) return;

    const csv = [
      ['BC-CRM Performance Report'],
      ['Generated', new Date().toLocaleString()],
      ['Period', `${filters.date_from || 'All time'} - ${filters.date_to || 'Today'}`],
      [''],
      ['KEY METRICS'],
      ['Total Visits', report.totalVisits],
      ['Unique PoS Reached', report.uniquePoS],
      ['New PoS Acquired', report.newPoSAcquired],
      ['Success Rate', report.successRate + '%'],
      ['Free Trays Committed', report.totalTraysCommitted],
      ['Free Trays Delivered', report.totalTraysDelivered],
      ['Delivery Fulfillment Rate', report.deliveryFulfillmentRate + '%'],
      [''],
      ['PRODUCT PERFORMANCE'],
      ['Product', 'Brand', 'Visits', 'Unique PoS', 'Success Rate', 'Trays'],
      ...((report.byProduct || []).map((p: any) => [
        p.name, p.brand, p.visits, p.uniquePoS, p.successRate + '%', p.trays
      ])),
      [''],
      ['GEOGRAPHIC COVERAGE'],
      ['City', 'PoS Count', 'Visit Count'],
      ...((report.geographicCoverage || []).map((g: any) => [
        g.city, g.posCount, g.visitCount
      ])),
    ].map(row => row.join(',')).join('\n');

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bc-crm-report-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const MetricCard = ({ title, value, subtitle, icon: Icon, color, trend }: any) => (
    <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm hover:shadow-md transition-shadow">
      <div className="flex justify-between items-start mb-3">
        <div className={`p-3 rounded-xl ${color}`}>
          <Icon size={24} className="text-white" />
        </div>
        {trend && (
          <span className={`text-xs font-bold px-2 py-1 rounded ${
            trend > 0 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {trend > 0 ? '↑' : '→'} {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-sm font-bold text-gray-500 mb-1">{title}</p>
      <p className="text-4xl font-black text-gray-900 tracking-tight">{value}</p>
      {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );

  // Format date range for display
  const formatDateRange = () => {
    if (filters.date_from && filters.date_to) {
      return `${new Date(filters.date_from).toLocaleDateString('de-DE')} - ${new Date(filters.date_to).toLocaleDateString('de-DE')}`;
    } else if (filters.date_from) {
      return `From ${new Date(filters.date_from).toLocaleDateString('de-DE')}`;
    } else if (filters.date_to) {
      return `Until ${new Date(filters.date_to).toLocaleDateString('de-DE')}`;
    }
    return 'All Time Data';
  };

  return (
    <>
      {/* Print-only CSS */}
      <style>{`
        @media print {
          /* FIRST PRIORITY: Hide no-print elements with maximum specificity */
          .no-print {
            display: none !important;
            visibility: hidden !important;
            position: absolute !important;
            left: -99999px !important;
            width: 0 !important;
            height: 0 !important;
          }
          
          .no-print,
          .no-print *,
          .no-print > *,
          .no-print > * > * {
            display: none !important;
            visibility: hidden !important;
          }
          
          /* CRITICAL: Hide scrollbars completely */
          *::-webkit-scrollbar {
            display: none !important;
            width: 0 !important;
            height: 0 !important;
          }
          
          *, *::before, *::after {
            overflow: visible !important;
            -webkit-overflow-scrolling: auto !important;
            -ms-overflow-style: none !important;
            scrollbar-width: none !important;
            max-height: none !important;
            height: auto !important;
            min-height: 0 !important;
            clip: auto !important;
            clip-path: none !important;
            transform: none !important;
          }
          
          html {
            overflow: visible !important;
            height: auto !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
          }
          
          body {
            background: white !important;
            overflow: visible !important;
            height: auto !important;
            min-height: auto !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            position: static !important;
          }
          
          #root {
            overflow: visible !important;
            height: auto !important;
            min-height: auto !important;
            width: 100% !important;
          }
          
          /* Ensure all parent containers allow full content */
          body > div,
          body > div > div,
          #root > *,
          main,
          main > * {
            overflow: visible !important;
            height: auto !important;
            min-height: auto !important;
            max-height: none !important;
          }
          
          /* Hide EVERYTHING except our report */
          body > *:not(#root),
          body > div:not(#root),
          /* Layout components */
          .md\\:hidden.bg-white.border-b,
          .hidden.md\\:flex,
          .md\\:hidden.fixed.bottom-0,
          /* Generic navigation/header/footer */
          nav,
          nav *,
          header,
          header *,
          footer,
          footer *,
          aside,
          .sidebar,
          .no-print,
          .no-print *,
          button,
          button *,
          input,
          select,
          textarea,
          .filter-section,
          [role="navigation"],
          [role="navigation"] *,
          [role="banner"],
          [role="banner"] *,
          [role="contentinfo"],
          [role="contentinfo"] *,
          [role="complementary"],
          [class*="sidebar"],
          [class*="nav"],
          [class*="header"],
          [class*="footer"],
          /* Specific layout elements */
          [class*="fixed"],
          [class*="sticky"],
          .z-20,
          .z-30 {
            display: none !important;
            visibility: hidden !important;
            position: absolute !important;
            left: -99999px !important;
            top: -99999px !important;
            height: 0 !important;
            width: 0 !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: hidden !important;
            opacity: 0 !important;
          }
          
          /* Show only our content - be VERY specific */
          #root,
          #root > div,
          #root > div > div,
          .print-cover-page,
          .print-only,
          .bg-white,
          .rounded-2xl,
          .space-y-8,
          .space-y-6,
          .space-y-4,
          .grid {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
          }
          
          /* Grid must display as grid */
          .grid {
            display: grid !important;
          }
          
          /* Flex must display as flex */
          .flex,
          .print-cover-page {
            display: flex !important;
          }
          
          /* Show print-only elements */
          .print-only {
            display: block !important;
          }
          
          .print-cover-page {
            display: flex !important;
            page-break-after: always !important;
            height: auto !important;
            min-height: 0 !important;
            margin: 0 !important;
            padding: 1.5rem 1rem !important;
            box-sizing: border-box;
          }
          
          /* Compact cover page elements */
          .print-cover-page .space-y-8 > * + * {
            margin-top: 0.75rem !important;
          }
          
          .print-cover-page .space-y-12 > * + * {
            margin-top: 1rem !important;
          }
          
          .print-cover-page .text-6xl {
            font-size: 2.5rem !important;
          }
          
          .print-cover-page .text-4xl {
            font-size: 1.75rem !important;
          }
          
          .print-cover-page .text-3xl {
            font-size: 1.5rem !important;
          }
          
          /* Page setup - minimal margins for maximum content */
          @page {
            size: A4;
            margin: 10mm 12mm;
          }
          
          /* Main content container - remove ALL constraints */
          main,
          main > div,
          .flex-1,
          .flex,
          .flex-col,
          .md\\:pl-64,
          .overflow-y-auto,
          .max-w-7xl,
          .mx-auto {
            max-width: 100% !important;
            max-height: none !important;
            height: auto !important;
            min-height: 0 !important;
            padding: 0 !important;
            margin: 0 !important;
            padding-left: 0 !important;
            margin-left: 0 !important;
            padding-bottom: 0 !important;
            margin-bottom: 0 !important;
            overflow: visible !important;
            flex: none !important;
          }
          
          /* Ensure all content containers are visible */
          .bg-white,
          .rounded-2xl,
          .rounded-xl,
          .border-2,
          .border,
          .shadow-sm {
            max-height: none !important;
            height: auto !important;
            overflow: visible !important;
          }
          
          /* Page break controls - ALLOW breaks to show all content */
          .page-break-avoid {
            page-break-inside: auto !important;
            break-inside: auto !important;
            overflow: visible !important;
          }
          
          .page-break-before {
            page-break-before: auto !important;
            margin-top: 0.5rem !important;
          }
          
          /* Allow content to flow naturally */
          .grid,
          .grid > *,
          div,
          section {
            page-break-inside: auto !important;
            break-inside: auto !important;
          }
          
          /* Preserve colors for client presentation */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          
          /* Grid optimizations */
          .grid-cols-4 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          
          .grid-cols-1.md\\:grid-cols-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          
          .grid-cols-1.lg\\:grid-cols-2 {
            grid-template-columns: repeat(2, 1fr) !important;
          }
          
          /* Ensure grids don't hide content */
          .grid {
            display: grid !important;
          }
          
          /* Remove unnecessary backgrounds */
          .bg-gray-50,
          .bg-gray-100 {
            background: transparent !important;
          }
          
          /* Typography optimization - very compact */
          body {
            font-size: 8.5pt !important;
            line-height: 1.2 !important;
          }
          
          h1 { 
            font-size: 16pt !important;
            margin-bottom: 0.25rem !important;
          }
          h2 { 
            font-size: 12pt !important;
            margin-bottom: 0.25rem !important;
          }
          h3 { 
            font-size: 10pt !important;
            margin-bottom: 0.25rem !important;
          }
          
          /* Smaller text sizes */
          .text-4xl {
            font-size: 1.25rem !important;
          }
          
          .text-3xl {
            font-size: 1.1rem !important;
          }
          
          .text-2xl {
            font-size: 1rem !important;
          }
          
          .text-xl {
            font-size: 0.95rem !important;
          }
          
          .text-lg {
            font-size: 0.9rem !important;
          }
          
          .text-sm {
            font-size: 0.75rem !important;
          }
          
          .text-xs {
            font-size: 0.65rem !important;
          }
          
          /* Reduce spacing to fit more content - VERY compact */
          .space-y-8 > * + * {
            margin-top: 0.5rem !important;
          }
          
          .space-y-6 > * + * {
            margin-top: 0.4rem !important;
          }
          
          .space-y-4 > * + * {
            margin-top: 0.3rem !important;
          }
          
          .space-y-3 > * + * {
            margin-top: 0.25rem !important;
          }
          
          .space-y-2 > * + * {
            margin-top: 0.15rem !important;
          }
          
          /* Compact padding */
          .p-8 {
            padding: 0.5rem !important;
          }
          
          .p-6 {
            padding: 0.4rem !important;
          }
          
          .p-4 {
            padding: 0.3rem !important;
          }
          
          /* Compact gaps */
          .gap-8 {
            gap: 0.5rem !important;
          }
          
          .gap-6 {
            gap: 0.4rem !important;
          }
          
          .gap-4 {
            gap: 0.3rem !important;
          }
          
          .gap-3 {
            gap: 0.25rem !important;
          }
          
          .gap-2 {
            gap: 0.15rem !important;
          }
          
          /* Make sure tables don't break */
          table, tr, td, th {
            page-break-inside: avoid !important;
          }
          
          /* Ensure ALL content is visible - override ALL hiding */
          .hidden:not(.print-cover-page) {
            display: none !important;
          }
          
          .print-cover-page.hidden {
            display: flex !important;
          }
          
          /* Force all report sections to display */
          [class*="space-y"] > div,
          [class*="grid"] > div,
          .bg-white {
            display: block !important;
            visibility: visible !important;
            position: static !important;
          }
          
          /* Ensure flex containers work */
          .flex {
            display: flex !important;
          }
          
          /* Ensure grid containers work */
          .grid {
            display: grid !important;
          }
          
          /* Remove shadows to save ink */
          .shadow-sm,
          .shadow-lg,
          .shadow-xl {
            box-shadow: none !important;
          }
          
          /* Compact specific sections */
          .mb-6 {
            margin-bottom: 0.5rem !important;
          }
          
          .mb-4 {
            margin-bottom: 0.35rem !important;
          }
          
          .mb-2 {
            margin-bottom: 0.25rem !important;
          }
          
          /* Make borders thinner */
          .border-2 {
            border-width: 1px !important;
          }
          
          .rounded-2xl {
            border-radius: 0.5rem !important;
          }
          
          .rounded-xl {
            border-radius: 0.375rem !important;
          }
          
          /* CRITICAL: Force display ALL divs inside report container */
          .space-y-8 {
            display: block !important;
            height: auto !important;
          }
          
          .space-y-8 > * {
            display: block !important;
            visibility: visible !important;
            opacity: 1 !important;
            position: static !important;
          }
          
          /* Prevent any truncation */
          [class*="truncate"],
          [class*="line-clamp"] {
            overflow: visible !important;
            text-overflow: clip !important;
            white-space: normal !important;
            -webkit-line-clamp: unset !important;
          }
        }
        
        @media screen {
          .print-only,
          .print-cover-page {
            display: none !important;
          }
        }
      `}</style>

      {/* Print Cover Page */}
      <div className="print-cover-page hidden flex-col justify-center items-center bg-white">
        <div className="text-center space-y-8 max-w-4xl px-12">
          {/* Logo */}
          <div className="space-y-3">
            <h1 className="text-6xl font-black text-gray-900">BC-CRM</h1>
            <div className="h-1.5 w-24 bg-brand-500 mx-auto rounded-full"></div>
          </div>
          
          {/* Report Title */}
          <div className="space-y-4 py-6">
            <h2 className="text-4xl font-black text-gray-800">
              Field Sales Performance Report
            </h2>
            <div className="text-2xl font-bold text-brand-600">
              {clientName}
            </div>
          </div>
          
          {/* Date Range */}
          <div className="bg-gradient-to-r from-brand-500 to-purple-600 text-white p-6 rounded-xl">
            <p className="text-xs font-bold opacity-90 mb-1">Reporting Period</p>
            <p className="text-3xl font-black">
              {formatDateRange()}
            </p>
          </div>
          
          {/* Metadata */}
          <div className="pt-8 space-y-2">
            <div className="h-px w-full bg-gray-200"></div>
            <p className="text-xs font-bold text-gray-600">
              Generated on {new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
              })}
            </p>
            <p className="text-[10px] text-gray-400 pt-2">
              BC-CRM Field Sales Analytics • Confidential Report
            </p>
          </div>
        </div>
      </div>

      {/* Screen-only UI wrapper */}
      <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 no-print">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
        <div>
          <h1 className="text-4xl font-black text-gray-900 tracking-tight">Analytics Dashboard</h1>
          <div className="flex items-center gap-3">
            <p className="text-gray-500 font-medium">Performance metrics and campaign insights</p>
            {report?.generatedAt && (
              <span className="text-xs text-gray-400">
                • Updated: {new Date(report.generatedAt).toLocaleTimeString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={exportCSV}
            disabled={!report}
            className="flex items-center space-x-2 bg-green-600 text-white px-6 py-3 rounded-xl font-bold hover:bg-green-700 transition-all active:scale-95 disabled:opacity-50"
          >
            <Download size={18} />
            <span>CSV</span>
          </button>
          <button
            onClick={() => window.print()}
            disabled={!report}
            className="flex items-center space-x-2 bg-gray-900 text-white px-6 py-3 rounded-xl font-bold hover:bg-black transition-all active:scale-95 disabled:opacity-50"
          >
            <Download size={18} />
            <span>PDF</span>
          </button>
        </div>
      </div>

      {/* Special Reports */}
      <div className="bg-gradient-to-r from-purple-500 to-blue-500 p-6 rounded-2xl shadow-lg text-white cursor-pointer hover:shadow-xl transition-all" onClick={() => navigate('/reports/distributor-orders')}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white bg-opacity-20 rounded-xl">
              <Truck size={32} />
            </div>
            <div>
              <h3 className="text-xl font-black mb-1">Lieferanten-Bestellreport</h3>
              <p className="text-sm opacity-90">Export orders grouped by distributor for easy forwarding</p>
            </div>
          </div>
          <div className="flex items-center gap-2 bg-white text-purple-600 px-4 py-2 rounded-lg font-bold hover:bg-opacity-90 transition-colors">
            <FileText size={18} />
            <span>Open</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-6 rounded-2xl border-2 border-gray-100 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Filter size={20} className="text-brand-600" />
            <h3 className="font-black text-gray-900">Filters</h3>
          </div>
          <button
            onClick={() => fetchReport()}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
            <span className="font-bold text-sm">Refresh</span>
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Product</label>
            <ProductSelector 
              value={filters.product_id}
              onChange={(val) => setFilters({...filters, product_id: val})}
              hideLabel={true}
              className="w-full p-3 border-2 border-gray-200 rounded-lg font-bold focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">Outcome</label>
            <select
              value={filters.outcome}
              onChange={(e) => setFilters({...filters, outcome: e.target.value})}
              className="w-full p-3 border-2 border-gray-200 rounded-lg font-bold focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors"
            >
              <option value="">All Outcomes</option>
              <option value="placement_secured">Placement Secured</option>
              <option value="rejected">Rejected</option>
              <option value="competitor_exclusive">Competitor Exclusive</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">From Date</label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setFilters({...filters, date_from: e.target.value})}
              className="w-full p-3 border-2 border-gray-200 rounded-lg font-bold focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-black text-gray-500 uppercase tracking-wider mb-2">To Date</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setFilters({...filters, date_to: e.target.value})}
              className="w-full p-3 border-2 border-gray-200 rounded-lg font-bold focus:border-brand-500 focus:ring-2 focus:ring-brand-200 transition-colors"
            />
          </div>
        </div>
      </div>
      </div>

      {/* Report Content - Print Only Wrapper */}
      {loading ? (
        <div className="text-center py-20 no-print">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-brand-600 mx-auto mb-4"></div>
          <p className="font-black text-gray-400 uppercase tracking-widest text-sm">Analyzing data...</p>
        </div>
      ) : report ? (
        <div className="max-w-7xl mx-auto p-4 md:p-8 space-y-8 print:space-y-4 print:p-0">
        <div className="space-y-8">
          {/* Phase 1: Core KPI Cards (4 big metrics) */}
          <div>
            <h2 className="text-2xl font-black text-gray-900 mb-4">Key Performance Indicators</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <MetricCard
                title="Total Visits"
                value={report.totalVisits}
                subtitle="Product-visits (each product counted separately)"
                icon={TrendingUp}
                color="bg-brand-500"
              />
              <MetricCard 
                title="PoS Reached" 
                value={report.uniquePoS} 
                subtitle={`${report.newPoSAcquired} first-time`}
                icon={Users} 
                color="bg-blue-500" 
              />
              <MetricCard 
                title="Success Rate" 
                value={report.successRate + '%'}
                subtitle="Placements secured"
                icon={Target} 
                color="bg-green-500" 
              />
              <MetricCard 
                title="Free Trays" 
                value={report.totalTraysCommitted}
                subtitle="Cumulative: Total across all visits"
                icon={Package} 
                color="bg-purple-500" 
              />
            </div>

            {/* Data Methodology Info */}
            <InfoBox>
              <strong>📊 Data Methodology:</strong> Placement data (facings, stock levels) shows the <strong>current state</strong> from the latest visit per product per PoS.
              Sales metrics (units sold, free trays) show <strong>cumulative totals</strong> across all visits.
            </InfoBox>
          </div>

          {/* Conversion Funnel */}
          <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
            <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
              <TrendingUp className="text-brand-600" size={24} />
              <span>Conversion Funnel</span>
            </h3>
            
            {report.conversionFunnel ? (
              <div className="space-y-6">
                {/* Funnel Stages */}
                <div className="space-y-4">
                  {/* Stage 1: Total Visits */}
                  <div className="relative">
                    <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-6 rounded-xl shadow-lg">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <Users size={32} />
                          <div>
                            <div className="text-sm font-bold opacity-90">Total Visits</div>
                            <div className="text-3xl font-black">{report.conversionFunnel.totalVisits}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-2xl font-black">100%</div>
                          <div className="text-xs opacity-90">of funnel</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="w-0.5 h-8 bg-gradient-to-b from-blue-600 to-green-500"></div>
                    </div>
                  </div>

                  {/* Stage 2: Placements Secured */}
                  <div className="relative">
                    <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-6 rounded-xl shadow-lg" style={{ width: `${report.conversionFunnel.placementRate}%`, minWidth: '200px' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <CheckCircle size={28} />
                          <div>
                            <div className="text-sm font-bold opacity-90">Placements Secured</div>
                            <div className="text-2xl font-black">{report.conversionFunnel.placementsSecured}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-black">{report.conversionFunnel.placementRate}%</div>
                          <div className="text-xs opacity-90">success rate</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="w-0.5 h-8 bg-gradient-to-b from-green-600 to-yellow-500"></div>
                    </div>
                  </div>

                  {/* Stage 3: Pending Delivery */}
                  <div className="relative">
                    <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-white p-5 rounded-xl shadow-lg" style={{ width: `${Math.max(30, (report.conversionFunnel.pendingDelivery / report.conversionFunnel.totalVisits) * 100)}%`, minWidth: '180px' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Package size={24} />
                          <div>
                            <div className="text-xs font-bold opacity-90">Pending Delivery</div>
                            <div className="text-xl font-black">{report.conversionFunnel.pendingDelivery}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-black">{((report.conversionFunnel.pendingDelivery / report.conversionFunnel.totalVisits) * 100).toFixed(1)}%</div>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-center">
                      <div className="w-0.5 h-8 bg-gradient-to-b from-yellow-600 to-purple-500"></div>
                    </div>
                  </div>

                  {/* Stage 4: Delivered */}
                  <div className="relative">
                    <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-5 rounded-xl shadow-lg" style={{ width: `${Math.max(25, (report.conversionFunnel.delivered / report.conversionFunnel.totalVisits) * 100)}%`, minWidth: '160px' }}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Truck size={24} />
                          <div>
                            <div className="text-xs font-bold opacity-90">Delivered</div>
                            <div className="text-xl font-black">{report.conversionFunnel.delivered}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-black">{report.conversionFunnel.deliveryRate}%</div>
                          <div className="text-[10px] opacity-90">of placements</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Summary Stats */}
                <div className="grid grid-cols-2 gap-4 pt-6 border-t-2 border-gray-200">
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <div className="text-2xl font-black text-green-600">{report.conversionFunnel.placementRate}%</div>
                    <div className="text-xs font-bold text-gray-600 mt-1">Placement Success Rate</div>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <div className="text-2xl font-black text-purple-600">{report.conversionFunnel.deliveryRate}%</div>
                    <div className="text-xs font-bold text-gray-600 mt-1">Delivery Fulfillment</div>
                  </div>
                </div>
              </div>
            ) : (
              <p className="text-center py-12 text-gray-400">No funnel data available</p>
            )}
          </div>

          {/* Product Performance Table */}
          <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
            <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
              <Package className="text-purple-600" size={24} />
              <span>Product Performance</span>
            </h3>
            
            {report.byProduct && report.byProduct.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b-2 border-gray-200">
                      <th className="text-left py-3 px-4 text-xs font-black text-gray-500 uppercase tracking-wider">Product</th>
                      <th className="text-center py-3 px-4 text-xs font-black text-gray-500 uppercase tracking-wider">Visits</th>
                      <th className="text-center py-3 px-4 text-xs font-black text-gray-500 uppercase tracking-wider">Unique PoS</th>
                      <th className="text-center py-3 px-4 text-xs font-black text-gray-500 uppercase tracking-wider">Success Rate</th>
                      <th className="text-right py-3 px-4 text-xs font-black text-gray-500 uppercase tracking-wider">Free Trays</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byProduct.map((product: any, index: number) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-4 px-4">
                          <div className="font-bold text-gray-900">{product.name}</div>
                          <div className="text-xs text-gray-500">{product.brand}</div>
                        </td>
                        <td className="text-center py-4 px-4 font-bold text-gray-900">{product.visits}</td>
                        <td className="text-center py-4 px-4 font-bold text-gray-700">{product.uniquePoS}</td>
                        <td className="text-center py-4 px-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full font-bold text-sm ${
                            parseFloat(product.successRate) >= 80 ? 'bg-green-100 text-green-700' :
                            parseFloat(product.successRate) >= 60 ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {product.successRate}%
                          </span>
                        </td>
                        <td className="text-right py-4 px-4 font-black text-purple-600">{product.trays}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-center py-12 text-gray-400">No product data available</p>
            )}
          </div>

          {/* Two Column Layout: Placements & Geography */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Placement Types */}
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
                <Archive className="text-brand-600" size={24} />
                <span>Placement Types</span>
              </h3>
              
              {report.placementTypes ? (
                <div className="space-y-4">
                  {[
                    { key: 'fridge', label: 'Fridge Placement', color: 'bg-blue-500', icon: '❄️' },
                    { key: 'display_stand', label: 'Display Stand', color: 'bg-purple-500', icon: '🎪' },
                    { key: 'checkout', label: 'Checkout', color: 'bg-green-500', icon: '💳' },
                    { key: 'poster', label: 'Poster Ads', color: 'bg-orange-500', icon: '📋' },
                    { key: 'general_shelf', label: 'General Shelf', color: 'bg-gray-500', icon: '📦' },
                  ].map(type => {
                    const count = parseInt(report.placementTypes[type.key] || '0');
                    const total = Object.values(report.placementTypes).reduce((sum: number, val: any) => sum + parseInt(val || '0'), 0);
                    const percentage = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
                    
                    return (
                      <div key={type.key}>
                        <div className="flex justify-between items-center text-sm mb-2">
                          <span className="font-bold text-gray-700 flex items-center gap-2">
                            <span>{type.icon}</span>
                            <span>{type.label}</span>
                          </span>
                          <span className="font-black text-gray-900">{count} <span className="text-xs text-gray-500">({percentage}%)</span></span>
                        </div>
                        <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                          <div 
                            className={`h-full ${type.color} transition-all`}
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                      </div>
                    );
                  })}
                  
                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <div className="flex justify-between items-center mb-1">
                      <span className="font-bold text-gray-700">Total Fridge Facings</span>
                      <span className="text-2xl font-black text-blue-600">{report.totalFridgeFacings}</span>
                    </div>
                    <p className="text-xs text-gray-500 italic">Current state from latest visit per PoS</p>
                  </div>
                </div>
              ) : (
                <p className="text-center py-12 text-gray-400">No placement data available</p>
              )}
            </div>

            {/* Geographic Coverage */}
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
                <MapPin className="text-green-600" size={24} />
                <span>Geographic Coverage</span>
              </h3>
              
              {report.geographicCoverage && report.geographicCoverage.length > 0 ? (
                <div className="space-y-3">
                  {report.geographicCoverage.map((city: any, index: number) => (
                    <div key={index} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                      <div className="flex justify-between items-center mb-2">
                        <span className="font-black text-gray-900 text-lg">{city.city || 'Unknown'}</span>
                        <div className="text-right">
                          <div className="text-sm font-bold text-brand-600">{city.visitCount} visits</div>
                          <div className="text-xs text-gray-500">{city.posCount} PoS</div>
                        </div>
                      </div>
                      <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className="h-full bg-green-500"
                          style={{ 
                            width: `${(city.visitCount / report.totalVisits) * 100}%` 
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center py-12 text-gray-400">No geographic data available</p>
              )}
            </div>
          </div>

          {/* Delivery Performance */}
          <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
            <h3 className="text-xl font-black text-gray-900 mb-6 flex items-center gap-2">
              <Truck className="text-purple-600" size={24} />
              <span>Delivery Performance</span>
            </h3>
            
            <div className="grid grid-cols-3 gap-6">
              <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl">
                <div className="text-sm font-bold text-gray-600 mb-2">TRAYS COMMITTED</div>
                <div className="text-4xl font-black text-blue-600">{report.totalTraysCommitted}</div>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl">
                <div className="text-sm font-bold text-gray-600 mb-2">TRAYS DELIVERED</div>
                <div className="text-4xl font-black text-green-600">{report.totalTraysDelivered}</div>
              </div>
              
              <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl">
                <div className="text-sm font-bold text-gray-600 mb-2">FULFILLMENT RATE</div>
                <div className="text-4xl font-black text-purple-600">{report.deliveryFulfillmentRate}%</div>
              </div>
            </div>
          </div>

          {/* Visit Type & Outcome Breakdown */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h3 className="text-xl font-black text-gray-900 mb-6">Visit Types</h3>
              <div className="space-y-3">
                {report.byVisitType?.map((vt: any) => {
                  const percentage = report.totalVisits > 0 ? ((parseInt(vt.count) / report.totalVisits) * 100).toFixed(0) : '0';
                  const typeLabels: any = {
                    'first_time': { label: 'First Visit', icon: '🎯', color: 'bg-green-500' },
                    'revisit': { label: 'Revisit', icon: '🔄', color: 'bg-orange-500' }
                  };
                  const typeInfo = typeLabels[vt.visit_type] || { label: vt.visit_type, icon: '📍', color: 'bg-gray-500' };
                  
                  return (
                    <div key={vt.visit_type}>
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="font-bold text-gray-700 flex items-center gap-2">
                          <span>{typeInfo.icon}</span>
                          <span>{typeInfo.label}</span>
                        </span>
                        <span className="font-black text-gray-900">{vt.count} <span className="text-xs text-gray-500">({percentage}%)</span></span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${typeInfo.color}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
              <h3 className="text-xl font-black text-gray-900 mb-6">Visit Outcomes</h3>
              <div className="space-y-3">
                {report.byOutcome?.map((outcome: any) => {
                  const percentage = report.totalVisits > 0 ? ((parseInt(outcome.count) / report.totalVisits) * 100).toFixed(0) : '0';
                  const outcomeColors: any = {
                    'placement_secured': { color: 'bg-green-500', label: 'Placement Secured' },
                    'rejected': { color: 'bg-red-500', label: 'Rejected' },
                    'follow_up_needed': { color: 'bg-yellow-500', label: 'Follow-up Needed' },
                    'competitor_exclusive': { color: 'bg-orange-500', label: 'Competitor Exclusive' }
                  };
                  const outcomeInfo = outcomeColors[outcome.visit_outcome] || { color: 'bg-gray-500', label: outcome.visit_outcome };
                  
                  return (
                    <div key={outcome.visit_outcome}>
                      <div className="flex justify-between items-center text-sm mb-2">
                        <span className="font-bold text-gray-700">{outcomeInfo.label}</span>
                        <span className="font-black text-gray-900">{outcome.count} <span className="text-xs text-gray-500">({percentage}%)</span></span>
                      </div>
                      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${outcomeInfo.color}`}
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Store Type Distribution */}
          <div className="bg-white p-8 rounded-2xl border-2 border-gray-100 shadow-sm">
            <h3 className="text-xl font-black text-gray-900 mb-6">Store Type Distribution</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {report.byStoreType?.map((st: any) => {
                const percentage = report.totalVisits > 0 ? ((parseInt(st.count) / report.totalVisits) * 100).toFixed(0) : '0';
                
                return (
                  <div key={st.store_type} className="p-6 bg-gray-50 rounded-2xl text-center hover:bg-brand-50 transition-colors">
                    <div className="text-4xl font-black text-brand-600 mb-2">{st.count}</div>
                    <div className="text-sm font-bold text-gray-700 capitalize mb-1">
                      {st.store_type?.replace('_', ' ')}
                    </div>
                    <div className="text-xs text-gray-500">{percentage}% of total</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        </div>
      ) : (
        <div className="max-w-7xl mx-auto p-4 md:p-8 no-print">
          <div className="text-center py-20 bg-white rounded-2xl border-2 border-dashed border-gray-200">
            <BarChart3 size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500 font-medium">No data available for the selected filters</p>
            <p className="text-sm text-gray-400 mt-2">Try adjusting your date range or removing filters</p>
          </div>
        </div>
      )}
    </>
  );
};

export default Reports;
